import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import {
  getFirestore,
  doc,
  collection,
  setDoc,
  deleteDoc,
  onSnapshot,
  getDocs,
  getDocFromServer
} from 'firebase/firestore';
import firebaseConfig from '../../firebase-applet-config.json';

// Initialize Firebase App
const app = initializeApp(firebaseConfig);

// CRITICAL: Must pass firebaseConfig.firestoreDatabaseId
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId);
export const auth = getAuth(app);

// Operation types for strict Firestore error handling
export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  };
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null): never {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map((provider) => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || [],
    },
    operationType,
    path,
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

// Connection test mandated by Firebase skill
async function testConnection() {
  try {
    await getDocFromServer(doc(db, 'test', 'connection'));
  } catch (error) {
    if (error instanceof Error && error.message.includes('the client is offline')) {
      console.warn('Firebase client is offline or starting up...');
    }
  }
}

if (typeof window !== 'undefined') {
  testConnection();
}

/**
 * Sanitize a word to make it a safe Firestore document ID
 */
export function wordToDocId(word: string): string {
  return encodeURIComponent((word || '').trim().toLowerCase()).replace(/%/g, '_');
}

// ==========================================
// 1. Audio Recordings Firestore Synchronization
// ==========================================

export async function saveAudioToFirestore(word: string, audioDataUrl: string): Promise<void> {
  const cleanWord = (word || '').trim().toLowerCase();
  if (!cleanWord || !audioDataUrl) return;
  const docId = wordToDocId(cleanWord);
  const docPath = `recorded_audios/${docId}`;

  try {
    await setDoc(doc(db, 'recorded_audios', docId), {
      word: cleanWord,
      audioDataUrl,
      recordedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, docPath);
  }
}

export async function deleteAudioFromFirestore(word: string): Promise<void> {
  const cleanWord = (word || '').trim().toLowerCase();
  if (!cleanWord) return;
  const docId = wordToDocId(cleanWord);
  const docPath = `recorded_audios/${docId}`;

  try {
    await deleteDoc(doc(db, 'recorded_audios', docId));
  } catch (error) {
    handleFirestoreError(error, OperationType.DELETE, docPath);
  }
}

export async function fetchAllAudiosFromFirestore(): Promise<Record<string, string>> {
  const path = 'recorded_audios';
  try {
    const snap = await getDocs(collection(db, path));
    const result: Record<string, string> = {};
    snap.forEach((d) => {
      const data = d.data();
      if (data && data.word && data.audioDataUrl) {
        result[data.word.toLowerCase().trim()] = data.audioDataUrl;
      }
    });
    return result;
  } catch (error) {
    handleFirestoreError(error, OperationType.LIST, path);
  }
}

export function subscribeFirestoreAudios(
  onUpdate: (recordings: Record<string, string>, changes: { added: Record<string, string>; removed: string[] }) => void
): () => void {
  const path = 'recorded_audios';
  const allMap: Record<string, string> = {};

  return onSnapshot(
    collection(db, path),
    (snapshot) => {
      const added: Record<string, string> = {};
      const removed: string[] = [];

      snapshot.docChanges().forEach((change) => {
        const data = change.doc.data();
        const word = (data.word || change.doc.id).toLowerCase().trim();

        if (change.type === 'removed') {
          delete allMap[word];
          removed.push(word);
        } else {
          if (data.audioDataUrl) {
            allMap[word] = data.audioDataUrl;
            added[word] = data.audioDataUrl;
          }
        }
      });

      onUpdate({ ...allMap }, { added, removed });
    },
    (error) => {
      handleFirestoreError(error, OperationType.GET, path);
    }
  );
}

// ==========================================
// 2. Title Artwork Firestore Synchronization
// ==========================================

export async function saveArtworkToFirestore(dataUrl: string): Promise<void> {
  const docPath = 'settings/artwork';
  try {
    await setDoc(doc(db, 'settings', 'artwork'), {
      dataUrl,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, docPath);
  }
}

export async function deleteArtworkFromFirestore(): Promise<void> {
  const docPath = 'settings/artwork';
  try {
    await deleteDoc(doc(db, 'settings', 'artwork'));
  } catch (error) {
    handleFirestoreError(error, OperationType.DELETE, docPath);
  }
}

export function subscribeFirestoreArtwork(onUpdate: (dataUrl: string | null) => void): () => void {
  const docPath = 'settings/artwork';
  return onSnapshot(
    doc(db, 'settings', 'artwork'),
    (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        onUpdate(data?.dataUrl || null);
      } else {
        onUpdate(null);
      }
    },
    (error) => {
      handleFirestoreError(error, OperationType.GET, docPath);
    }
  );
}

// ==========================================
// 3. Word Overrides Firestore Synchronization
// ==========================================

export interface FirestoreWordOverrideItem {
  kab: string;
  fr: string;
  icon?: string;
  type?: string;
  isNew?: boolean;
  isDeleted?: boolean;
  gender?: 'm' | 'f' | 'none';
  grammaticalNumber?: 'sg' | 'pl';
  pluralKab?: string;
  singularKab?: string;
  feminineKab?: string;
  masculineKab?: string;
  variants?: string;
  conjugationAccompli?: string;
  conjugationInaccompli?: string;
}

export async function saveWordOverrideToFirestore(
  word: string,
  override: {
    kab: string;
    fr: string;
    icon?: string;
    type?: string;
    isNew?: boolean;
    isDeleted?: boolean;
    gender?: 'm' | 'f' | 'none';
    grammaticalNumber?: 'sg' | 'pl';
    pluralKab?: string;
    singularKab?: string;
    feminineKab?: string;
    masculineKab?: string;
    variants?: string;
    conjugationAccompli?: string;
    conjugationInaccompli?: string;
  }
): Promise<void> {
  const originalKey = (word || '').trim();
  const lowerKey = originalKey.toLowerCase();
  if (!lowerKey) return;
  const docId = wordToDocId(lowerKey);
  const docPath = `word_overrides/${docId}`;

  try {
    await setDoc(doc(db, 'word_overrides', docId), {
      word: lowerKey,
      originalKey: originalKey,
      french: override.fr,
      pronunciation: override.kab,
      category: override.icon || '',
      type: override.type || 'Général',
      isNew: Boolean(override.isNew),
      isDeleted: Boolean(override.isDeleted),
      gender: override.gender || 'none',
      grammaticalNumber: override.grammaticalNumber || '',
      pluralKab: override.pluralKab || '',
      singularKab: override.singularKab || '',
      feminineKab: override.feminineKab || '',
      masculineKab: override.masculineKab || '',
      variants: override.variants || '',
      conjugationAccompli: override.conjugationAccompli || '',
      conjugationInaccompli: override.conjugationInaccompli || '',
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, docPath);
  }
}

export async function deleteWordOverrideFromFirestore(word: string): Promise<void> {
  const cleanWord = (word || '').trim().toLowerCase();
  if (!cleanWord) return;
  const docId = wordToDocId(cleanWord);
  const docPath = `word_overrides/${docId}`;

  try {
    await deleteDoc(doc(db, 'word_overrides', docId));
  } catch (error) {
    handleFirestoreError(error, OperationType.DELETE, docPath);
  }
}

export async function fetchAllWordOverridesFromFirestore(): Promise<Record<string, FirestoreWordOverrideItem>> {
  const path = 'word_overrides';
  try {
    const snap = await getDocs(collection(db, path));
    const result: Record<string, FirestoreWordOverrideItem> = {};
    snap.forEach((d) => {
      const data = d.data();
      if (data) {
        const origKey = (data.originalKey || data.word || d.id).trim();
        const lowerKey = (data.word || origKey || d.id).toLowerCase().trim();
        const item: FirestoreWordOverrideItem = {
          kab: data.pronunciation || data.word || origKey,
          fr: data.french || '',
          icon: data.category || undefined,
          type: data.type || 'Général',
          isNew: Boolean(data.isNew),
          isDeleted: Boolean(data.isDeleted),
          gender: data.gender === 'm' || data.gender === 'f' ? data.gender : (data.gender === 'none' ? 'none' : undefined),
          grammaticalNumber: data.grammaticalNumber === 'sg' || data.grammaticalNumber === 'pl' ? data.grammaticalNumber : undefined,
          pluralKab: data.pluralKab !== undefined ? String(data.pluralKab).trim() : undefined,
          singularKab: data.singularKab !== undefined ? String(data.singularKab).trim() : undefined,
          feminineKab: data.feminineKab !== undefined ? String(data.feminineKab).trim() : undefined,
          masculineKab: data.masculineKab !== undefined ? String(data.masculineKab).trim() : undefined,
          variants: data.variants ? String(data.variants).trim() : undefined,
          conjugationAccompli: data.conjugationAccompli ? String(data.conjugationAccompli).trim() : undefined,
          conjugationInaccompli: data.conjugationInaccompli ? String(data.conjugationInaccompli).trim() : undefined,
        };
        result[origKey] = item;
        result[lowerKey] = item;
      }
    });
    return result;
  } catch (error) {
    handleFirestoreError(error, OperationType.LIST, path);
  }
}

export function subscribeFirestoreWordOverrides(
  onUpdate: (overrides: Record<string, FirestoreWordOverrideItem>) => void
): () => void {
  const path = 'word_overrides';

  return onSnapshot(
    collection(db, path),
    (snapshot) => {
      const map: Record<string, FirestoreWordOverrideItem> = {};
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        if (data) {
          const origKey = (data.originalKey || data.word || docSnap.id).trim();
          const lowerKey = (data.word || origKey || docSnap.id).toLowerCase().trim();
          const item: FirestoreWordOverrideItem = {
            kab: data.pronunciation || data.word || origKey,
            fr: data.french || '',
            icon: data.category || undefined,
            type: data.type || 'Général',
            isNew: Boolean(data.isNew),
            isDeleted: Boolean(data.isDeleted),
            gender: data.gender === 'm' || data.gender === 'f' ? data.gender : (data.gender === 'none' ? 'none' : undefined),
            grammaticalNumber: data.grammaticalNumber === 'sg' || data.grammaticalNumber === 'pl' ? data.grammaticalNumber : undefined,
            pluralKab: data.pluralKab !== undefined ? String(data.pluralKab).trim() : undefined,
            singularKab: data.singularKab !== undefined ? String(data.singularKab).trim() : undefined,
            feminineKab: data.feminineKab !== undefined ? String(data.feminineKab).trim() : undefined,
            masculineKab: data.masculineKab !== undefined ? String(data.masculineKab).trim() : undefined,
            variants: data.variants ? String(data.variants).trim() : undefined,
            conjugationAccompli: data.conjugationAccompli ? String(data.conjugationAccompli).trim() : undefined,
            conjugationInaccompli: data.conjugationInaccompli ? String(data.conjugationInaccompli).trim() : undefined,
          };
          map[origKey] = item;
          map[lowerKey] = item;
        }
      });
      onUpdate(map);
    },
    (error) => {
      handleFirestoreError(error, OperationType.GET, path);
    }
  );
}
